/**
 * Time Frame Helper Utilities
 *
 * Uses the offset fields from the period-weeks API response to derive
 * date ranges for each time frame option. The offsets are relative to today:
 *   - weekOffset: 0 = current week, -1 = last closed week
 *   - periodOffset: 0 = current period, -1 = last closed period
 *   - quarterOffset: 0 = current quarter
 *   - yearOffset: 0 = current year
 *   - dayOffset: 0 = today, negative = past, positive = future
 */

import type { YearData, DayData, CurrentFiscalContext } from '../../../api/periodWeeksApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimeFrameKey =
	| 'WEEK'
	| 'WEEK_TO_DATE'
	| 'PERIOD'
	| 'PERIOD_TO_DATE'
	| 'QUARTER'
	| 'QUARTER_TO_DATE'
	| 'YEAR'
	| 'YEAR_TO_DATE'
	| 'LAST_CLOSED_WEEK'
	| 'LAST_CLOSED_PERIOD';

export interface TimeFrameDateRange {
	/** Start date ISO string (first day of the range) */
	startDate: string;
	/** End date ISO string (last day of the range) */
	endDate: string;
	/** All dates in the range, sorted chronologically */
	dates: string[];
	/** The year(s) covered */
	years: number[];
	/** The period(s) covered */
	periods: number[];
	/** The weekInYear(s) covered */
	weekInYears: number[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Flatten all days from yearData into a single array with context (year, period, weekInYear).
 */
interface FlatDay extends DayData {
	year: number;
	period: number;
	weekInYear: number;
}

function flattenDays(yearData: YearData[]): FlatDay[] {
	const days: FlatDay[] = [];
	for (const yd of yearData) {
		for (const pd of yd.periods) {
			for (const weekObj of pd.weeks) {
				if (weekObj.days) {
					for (const day of weekObj.days) {
						days.push({
							...day,
							year: yd.year,
							period: pd.period,
							weekInYear: weekObj.weekInYear,
						});
					}
				}
			}
		}
	}
	return days;
}

/**
 * Filter days based on offset criteria and build a TimeFrameDateRange.
 */
function buildRange(days: FlatDay[]): TimeFrameDateRange | null {
	if (days.length === 0) return null;

	const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
	const dates = sorted.map(d => d.date);
	const years = [...new Set(sorted.map(d => d.year))];
	const periods = [...new Set(sorted.map(d => d.period))];
	const weekInYears = [...new Set(sorted.map(d => d.weekInYear))];

	return {
		startDate: dates[0],
		endDate: dates[dates.length - 1],
		dates,
		years,
		periods,
		weekInYears,
	};
}

// ─── Main Function ────────────────────────────────────────────────────────────

/**
 * Get the date range for a given time frame key using offset fields from the API data.
 *
 * @param yearData - The full period-weeks API response data
 * @param timeFrame - The time frame key to compute
 * @returns TimeFrameDateRange or null if no matching days found
 */
export function getTimeFrameDateRange(
	yearData: YearData[],
	timeFrame: TimeFrameKey
): TimeFrameDateRange | null {
	if (!yearData || yearData.length === 0) return null;

	const allDays = flattenDays(yearData);

	// ─── Diagnostic logging (TEMPORARY — remove after debugging) ─────────────
	console.log('[TimeFrame Debug] Total flattened days:', allDays.length);
	console.log('[TimeFrame Debug] Unique periods:', [...new Set(allDays.map(d => d.period))]);
	console.log('[TimeFrame Debug] Unique weekInYears count:', [...new Set(allDays.map(d => d.weekInYear))].length);
	console.log('[TimeFrame Debug] yearOffset values:', [...new Set(allDays.map(d => d.yearOffset))]);
	console.log('[TimeFrame Debug] dayOffset range:', Math.min(...allDays.map(d => d.dayOffset ?? 999)), 'to', Math.max(...allDays.map(d => d.dayOffset ?? -999)));

	let filtered: FlatDay[];

	switch (timeFrame) {
		case 'WEEK':
			filtered = allDays.filter(d => d.weekOffset === 0);
			break;
		case 'WEEK_TO_DATE':
			filtered = allDays.filter(d => d.weekOffset === 0 && (d.dayOffset ?? 1) <= 0);
			break;
		case 'PERIOD':
			filtered = allDays.filter(d => d.periodOffset === 0);
			break;
		case 'PERIOD_TO_DATE':
			filtered = allDays.filter(d => d.periodOffset === 0 && (d.dayOffset ?? 1) <= 0);
			break;
		case 'QUARTER':
			filtered = allDays.filter(d => d.quarterOffset === 0);
			break;
		case 'QUARTER_TO_DATE':
			filtered = allDays.filter(d => d.quarterOffset === 0 && (d.dayOffset ?? 1) <= 0);
			break;
		case 'YEAR':
			filtered = allDays.filter(d => d.yearOffset === 0);
			break;
		case 'YEAR_TO_DATE':
			filtered = allDays.filter(d => d.yearOffset === 0 && (d.dayOffset ?? 1) <= 0);
			break;
		case 'LAST_CLOSED_WEEK':
			filtered = allDays.filter(d => d.weekOffset === -1);
			break;
		case 'LAST_CLOSED_PERIOD':
			filtered = allDays.filter(d => d.periodOffset === -1);
			break;
		default:
			filtered = [];
	}

	// ─── Diagnostic logging for filtered result (TEMPORARY) ──────────────────
	console.log(`[TimeFrame Debug] ${timeFrame} filtered days:`, filtered.length);
	console.log(`[TimeFrame Debug] ${timeFrame} filtered periods:`, [...new Set(filtered.map(d => d.period))]);
	console.log(`[TimeFrame Debug] ${timeFrame} filtered weekInYears:`, [...new Set(filtered.map(d => d.weekInYear))]);

	return buildRange(filtered);
}

/**
 * Format a date range for display.
 * Single date: "Jul 15, 2026"
 * Range: "Jul 12 – Jul 15, 2026" (same year) or "Dec 28, 2025 – Jan 03, 2026"
 */
export function formatDateRangeDisplay(startDate: string, endDate: string): string {
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	const parseDate = (iso: string) => {
		const [y, m, d] = iso.split('-');
		return { year: y, month: months[parseInt(m, 10) - 1], day: parseInt(d, 10) };
	};

	const start = parseDate(startDate);
	const end = parseDate(endDate);

	if (startDate === endDate) {
		return `${start.month} ${start.day}, ${start.year}`;
	}

	if (start.year === end.year) {
		return `${start.month} ${start.day} – ${end.month} ${end.day}, ${end.year}`;
	}

	return `${start.month} ${start.day}, ${start.year} – ${end.month} ${end.day}, ${end.year}`;
}

/**
 * Get display label for a time frame key.
 */
export function getTimeFrameLabel(key: TimeFrameKey): string {
	const labels: Record<TimeFrameKey, string> = {
		WEEK: 'Week',
		WEEK_TO_DATE: 'Week-to-Date',
		PERIOD: 'Period',
		PERIOD_TO_DATE: 'Period-to-Date',
		QUARTER: 'Quarter',
		QUARTER_TO_DATE: 'Quarter-to-Date',
		YEAR: 'Year',
		YEAR_TO_DATE: 'Year-to-Date',
		LAST_CLOSED_WEEK: 'Last Closed Week',
		LAST_CLOSED_PERIOD: 'Last Closed Period',
	};
	return labels[key] || key;
}

/**
 * All available time frame options for the UI.
 */
export const TIME_FRAME_OPTIONS: TimeFrameKey[] = [
	'WEEK_TO_DATE',
	'PERIOD_TO_DATE',
	'QUARTER_TO_DATE',
	'YEAR_TO_DATE',
	'LAST_CLOSED_WEEK',
	'LAST_CLOSED_PERIOD',
];
